import { getBmi } from "../../../../lib/common/js/helpers"

// Helpers utils and logic to resolve conditions based on
// previously answered data from the user
export const initAnswersUtils = (store) => ({
  isMale() {
    return store.values.gender === 'male'
  },

  isFemale() {
    return store.values.gender === 'female'
  },

  isAgeRangeLevel(level) {
    return store.values.ageRange === `ageRange${level}`
  },

  isAgeRange(ageRange) {
    return store.values.ageRange === ageRange
  },

  isMetricSystem(measurementSystem) {
    return store.values.measurementSystem === measurementSystem
  },

  isDailyWaterIntakeLevel(level) {
    return store.values.dailyWaterIntake === `valueWaterIntakeLevel${level}`
  },

  isDietFamiliarity(dietFamiliarity) {
    const valuesToCheck = Array.isArray(dietFamiliarity) ? dietFamiliarity : [dietFamiliarity]

    return valuesToCheck.some((value) => value === store.values.dietFamiliarity)
  },

  isMetric() { return store.values.measurementSystem === "metric" },

  getTargetWeight() {
    return this.isMetric() ? store.values.targetWeightKg : store.values.targetWeightLbs
  },

  getBmi() {
    return getBmi(store.values, store.values.measurementSystem === "metric")
  },

  getBmiCategory() {
    const BMI = this.getBmi()

    if (!BMI) return null

    if (BMI < 18.5) {
      return "underweight"
    } else if (BMI >= 18.5 && BMI < 25) {
      return "normal"
    } else if (BMI >= 25 && BMI < 30) {
      return "overweight"
    } else if (BMI >= 30) {
      return "obese"
    }
  },

  isPrimaryGoal(goal) {
    // Expected values:
    // valueLoseWeight
    // valueImproveHealth
    // valueFirmAndToned
    // valueGetRidOfStress
    const goals = Array.isArray(goal) ? goal : [goal]
    return goals.every((i) => store.values.primaryGoal.includes(i))
  },

  isWeightGain() {
    const { weightKg, weightLbs, targetWeightKg, targetWeightLbs } = store.values
    const isMetric = store.isMetric()

    const currentWeight = isMetric ? weightKg : weightLbs
    const targetWeight = isMetric ? targetWeightKg : targetWeightLbs

    return targetWeight >= currentWeight
  },

  isHealthConditions(condition) {
    const conditions = Array.isArray(condition) ? condition : [condition]
    return conditions.every((i) => store.values.healthConditions.includes(i))
  },

  isNoHealthConditions() {
    return this.isHealthConditions('valueNone')
  },

  // We show detailed health conditions UI only if:
  // no "cancer" or no "something else" is selected
  isHealthConditionsUI() {
    const isHealthConditionsSelected = store.values.healthConditions?.length
    const isValueNone = this.isHealthConditions('valueNone')
    const isExcludedConditions = this.isOnlyHealthConditions(['valueCancer', 'valueSomethingElse'])

    return isHealthConditionsSelected && !isValueNone && !isExcludedConditions
  },

  isOnlyHealthConditions(conditions) {
    const selected = store.values.healthConditions || []
  
    // Check is only allowed items selected
    return (
      selected.length > 0 &&
      selected.every((i) => conditions.includes(i)) &&
      selected.length <= conditions.length
    )
  },

  getFastingIntroGoalFlow() {
    let flow = 1
    const isLoseWeight = this.isPrimaryGoal('valueLoseWeight')
    const isFirmAndToned = this.isPrimaryGoal('valueFirmAndToned')
    const isImproveHealth = this.isPrimaryGoal('valueImproveHealth')
    const isGetRidOfStress = this.isPrimaryGoal('valueGetRidOfStress')

    // Flow 1:
    // Lose weight
    // Lose weight + health 
    // Lose weight + stress
    // Lose weight + health + stress
    if (
      isLoseWeight &&
      !isFirmAndToned // Exclude "Lose weight + get firm" cause it goes to Flow 2 or 4
    ) {
      flow = 1
      return flow
    }

    // Flow 2:
    // Lose weight + get firm
    // Lose weight + get firm + stress
    // Lose weight + get firm + health
    if (
      isFirmAndToned &&
      !(
        isLoseWeight &&
        isImproveHealth &&
        isGetRidOfStress
      ) // excludes the full combo (goes to Flow 4)
    ) {
      flow = 2
      return flow
    }

    // Flow 3: 
    // Health 
    // Stress
    // Health + Stress 
    if (
      (isImproveHealth || isGetRidOfStress) &&
      !isLoseWeight &&
      !isFirmAndToned
    ) {
      flow = 3
      return flow
    }

    // Flow 4:
    // Lose weight + get firm + health + stress
    if (
      isLoseWeight &&
      isFirmAndToned &&
      isImproveHealth &&
      isGetRidOfStress
    ) {
      flow = 4
      return flow
    }

    return flow
  },

  getFastingIntroDietFlow() {
    let flow = 2

    if (this.isDietFamiliarity('valueTried')) {
      flow = 1
      return flow
    }

    if (this.isDietFamiliarity(['valueHeard', 'valueNever'])) {
      flow = 2
      return flow
    }

    return flow
  },

  getSocialProofWithReviewsFlow() {
    let flow = 3
    const isLoseWeight = this.isPrimaryGoal('valueLoseWeight')
    const isFirmAndToned = this.isPrimaryGoal('valueFirmAndToned')
    const isImproveHealth = this.isPrimaryGoal('valueImproveHealth')
    const isGetRidOfStress = this.isPrimaryGoal('valueGetRidOfStress')

    if ((isLoseWeight || isFirmAndToned) && !isImproveHealth && !isGetRidOfStress) {
      flow = 1
      return flow
    }

    if ((isImproveHealth || isGetRidOfStress) && !isLoseWeight && !isFirmAndToned) {
      flow = 2
      return flow
    }
    
    return flow
  },

  getSecondaryGoalFlow() {
    let flow = 1
    const isLoseWeight = this.isPrimaryGoal('valueLoseWeight')
    const isFirmAndToned = this.isPrimaryGoal('valueFirmAndToned')
    const isImproveHealth = this.isPrimaryGoal('valueImproveHealth')
    const isGetRidOfStress = this.isPrimaryGoal('valueGetRidOfStress')

    // Flow 1:
    // Goal of Lose weight (alone or in any combination with other answers)
    if (isLoseWeight) {
      flow = 1
      return flow
    }

    // Flow 2:
    // Any combination of: firm/toned, improve health, get rid of stress
    if (isFirmAndToned || isImproveHealth || isGetRidOfStress) {
      flow = 2
      return flow
    }
    
    return flow
  },

  getRhetoricalQuestionFlow() {
    let flow = 1
    const isLoseWeight = this.isPrimaryGoal('valueLoseWeight')
    const isFirmAndToned = this.isPrimaryGoal('valueFirmAndToned')
    const isImproveHealth = this.isPrimaryGoal('valueImproveHealth')
    const isGetRidOfStress = this.isPrimaryGoal('valueGetRidOfStress')

    // PO requirements based on primary goal:

    // Flow 1:
    // Lose weight as the only choice or with any other answer
    // OR get firm and toned as the only choice or with any other answer
    if (isLoseWeight || isFirmAndToned) {
      flow = 1
      return flow
    }

    // Flow 2:
    // Improve overall health (and, or) get rid of stress
    // BUT NO lose weight or get toned
    if ((isImproveHealth || isGetRidOfStress) && !(isLoseWeight || isFirmAndToned)) {
      flow = 2
      return flow
    }

    return flow
  },

  getResultsFlow() {
    let flow = 1
    const isWeightGain = this.isWeightGain()

    if (isWeightGain) {
      flow = 2
      return flow
    } else {
      flow = 1
      return flow
    }
  },

  getProjectedProgressFlow() {
    let flow = 1
    const isWeightGain = this.isWeightGain()
    const isOverweight = this.getBmiCategory() === 'overweight'
    const isObese = this.getBmiCategory() === 'obese'

    // Flow 1: goal weight < current weight
    if (!isWeightGain) {
      flow = 1
      return flow
    }

    // Flow 2: goal weight > current weight and current BMI is gray or green
    if (isWeightGain && !isOverweight && !isObese) {
      flow = 2
      return flow
    }

    // Flow 3: goal weight > current weight and current BMI is yellow or red (Overrides goal weight value)
    if (isWeightGain && (isOverweight || isObese)) {
      flow = 3
      return flow
    }

    return flow
  },

  getWeightGainFlow() {
    let flow = 1
    const isWeightGain = this.isWeightGain()

    if (isWeightGain) {
      flow = 2
      return flow
    }
    
    return flow
  },

  getPlanSelectionFlow() {
    let flow = 2
    const isLoseWeight = this.isPrimaryGoal('valueLoseWeight')
    const isWeightGain = this.isWeightGain()

    // PO requirements based on primary goal:

    // Flow 3: the weight gain scenario - takes priority over other conditions
    if (isWeightGain) {
      flow = 3
      return flow
    }

    // Flow 1: Lose weight OR Lose weight + any combination of other answers
    if (isLoseWeight && (!isWeightGain)) {
      flow = 1
      return flow
    }

    return flow
  }
})
